import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { usePageContext } from '@context/PageContext';
import { useUserContext } from '@context/UserContext';
import { createId } from '@paralleldrive/cuid2';
import { AuditHistory } from '@services/api/deckManagement/graphql/schema/API';
import {
  useGetAuditHistoryForDeck,
  useGetAuditHistoryForPage,
} from '@services/api/deckManagement/hooks/useAuditHistory';
import { useSaveDeck, UseSaveDeckParams } from '@services/api/deckManagement/hooks/useDeck';
import { entityKeyAsId } from '@utils/deckHelpers';
import { find, forEach, get, isEmpty, isNil } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ChangeSetTargets, SaveDeckChangeSetSchema, SaveDeckChangeSetType } from 'src/schema';
import { HistoryItemMetadata, HistoryItemType } from 'types/AuditHistory.types';
import {
  AuditHistoryChangeType,
  AuditHistoryContextType,
  AuditHistoryProviderProps,
  EnhancedAuditHistory,
} from 'types/AuditHistoryContext.types';
import { DECKLINE_FORMATTING_FIELD_AUDIT_LABELS, DecklineFormattingFields } from 'types/DecklineFormatting.types';
import { DeleteDecklineData, EnhancedDeckline } from 'types/DecklinesContext.types';
import { TOC_FORMATTING_FIELD_AUDIT_LABELS, TOCFormattingFields } from 'types/TOC.types';
import { DeleteTOCEntryData, EnhancedTOCEntry } from 'types/TOCContext.types';

/**
 * Audit History Context
 *
 * Reach context for Audit History state management.
 */
export const AuditHistoryContext = createContext<AuditHistoryContextType>({
  deckAuditHistory: [],
  pageAuditHistory: [],
  auditTrail: [],
  setDeckAuditHistory: () => {},
  setPageAuditHistory: () => {},
  setAuditTrail: () => {},
  generatePageLibraryAuditTrail: () => [],
  generateDecklineAuditTrail: () => [],
  saveAuditTrail: () => {},
  isSavingAuditHistory: false,
  isLoading: false,
  isError: false,
  error: null,
  fetchNextPageAuditHistory: () => {},
  hasNextPageAuditHistory: false,
  isFetchingNextPageAuditHistory: false,
});

export const AuditHistoryContextProvider = ({ children }: AuditHistoryProviderProps): ReactNode => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { username } = useUserContext();
  const { currentDeckId, currentDeck } = useDeckContext();
  const { currentGranularityPage, selectedGranularity } = usePageContext();
  const { toastNotification } = useAurigaContext();
  const currentPageId = currentGranularityPage ? entityKeyAsId(currentGranularityPage.pk) : null; // Non-null from Page View

  // Check if we are on a deck audit history route or if the deck history dialog is open via search params
  const isDeckAuditHistoryRoute = Boolean(location.pathname.match(/\/deck-preparation\/decks\/[^/]+\/audit-history$/));
  const isDeckAuditHistoryRequested = isDeckAuditHistoryRoute || searchParams.get('history') === 'deck';

  // State for Api returned audit history and error handling
  const [deckAuditHistory, setDeckAuditHistory] = useState<EnhancedAuditHistory[]>([]);
  const [pageAuditHistory, setPageAuditHistory] = useState<EnhancedAuditHistory[]>([]);
  // To persist the currently generated audit trail
  const [auditTrail, setAuditTrail] = useState<EnhancedAuditHistory[]>([]);
  // Track saving state
  const [isSaving, setIsSaving] = useState(false);

  // Save Deck mutation for saving audit trail
  const saveDeckMutation = useSaveDeck();

  // Fetch audit history for the current Deck as available
  const {
    data: deckAuditHistoryData,
    isLoading: isDeckAuditHistortLoading,
    isError: isDeckAuditHistoryError,
    error: deckAuditHistoryError,
  } = useGetAuditHistoryForDeck({
    deckId: currentDeckId || '', // To filter API results by loaded Deck
    options: {
      // Only fetch when we have a valid deckId and User is visiting the deck audit history route
      enabled: !isNil(currentDeckId) && !isEmpty(currentDeckId) && isDeckAuditHistoryRequested,
    },
  });

  // Fetch audit history for the loaded Page as available (paginated)
  const {
    data: pageAuditHistoryData,
    isLoading: isPageAuditHistortLoading,
    isError: isPageAuditHistoryError,
    error: pageAuditHistoryError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetAuditHistoryForPage({
    deckId: currentDeckId || '', // To filter API results by loaded Deck
    page_id: currentPageId || '', // To filter API results by loaded Page
    options: {
      // Only fetch when we have a valid pageName and deckId
      enabled: !isNil(currentDeckId) && !isEmpty(currentDeckId) && !isNil(currentPageId) && !isEmpty(currentPageId),
    },
  });

  // Eagerly fetch all pages so the full audit history is available for both PAGE_VIEW and
  // DECKLINE_VIEW (which filters client-side by decklineId and needs the complete dataset).
  // TODO: If getAuditHistoryForPage adds a deckline_id filter param, revisit this to switch
  //  to lazy load on demand — eager fetching would no longer be necessary for DECKLINE_VIEW.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /**
   * Resets any audit history data as needed to restore an empty context state
   */
  const resetAuditHistory = useCallback(() => {
    setDeckAuditHistory([]);
    setPageAuditHistory([]);
    setAuditTrail([]);
  }, []);

  // Union type for all auditable entities
  type AuditableEntity = EnhancedDeckline | DeleteDecklineData | EnhancedTOCEntry | DeleteTOCEntryData;

  /**
   * Creates a base audit history item for a given deckline and change type
   * @param entity - The entity to create an audit history item for
   * @param historyItemType - The category of audit history item we are adding (e.g. DecklineHistory, PageHistory)
   * @param changeType - The change type of the audit history item
   * @param createDateTime - The date and time of the audit history item
   * @returns The created audit history item or null if no page is available
   */
  const createBaseAuditHistoryItem = useCallback(
    (
      entity: AuditableEntity,
      historyItemType: HistoryItemType,
      changeType: AuditHistoryChangeType,
      createDateTime?: string,
    ): EnhancedAuditHistory | null => {
      // Create a new internal tracking id for Audit History Item
      const internalId = createId();
      // For formulating an Audit History ID
      let newAuditHistoryId: string;
      // For Deckline edit items from the Page View, we can rely on currentPageId
      // For Page library edit items, we can derive page_id from the TOC item itself
      let auditPageId = currentPageId;
      if (historyItemType === HistoryItemType.PAGE) {
        auditPageId = 'page_id' in entity && !isNil(entity.page_id) ? entity.page_id : null;
      }

      if (auditPageId) {
        // Shared across all History Item Types
        const coreAuditHistoryDetails = {
          __typename: 'AuditHistory' as const,
          change_type: changeType,
          dateTime: createDateTime || new Date().toISOString(),
          user: username,
          page_id: auditPageId,
        };

        // Deckline entity that accounts for all valid deckline history item types
        let decklineEntity: EnhancedDeckline | DeleteDecklineData | undefined;
        // TOC entity for possible history item types
        let tocEntity: EnhancedTOCEntry | DeleteTOCEntryData | undefined;
        switch (historyItemType) {
          case HistoryItemType.PAGE:
            if ('__typename' in entity) {
              const { __typename: typeName } = entity;
              if (typeName === 'TOCEntry') {
                tocEntity = entity as EnhancedTOCEntry;
              }
            } else {
              tocEntity = entity as DeleteTOCEntryData;
            }

            if (tocEntity) {
              newAuditHistoryId = `${tocEntity?.toc_id}=${internalId}`;
              return {
                ...coreAuditHistoryDetails,
                pk: 'pk' in tocEntity ? tocEntity.pk : `DELETED_PAGE#${tocEntity.toc_id}`, // Not sent to api. Purely to fulfill the requirement of AuditHistory entity
                sk: `AUDIT#${newAuditHistoryId}`, // Not sent to api.
                row: JSON.stringify({
                  itemId: tocEntity.toc_id,
                  itemType: historyItemType,
                  displayName: 'page_name' in tocEntity ? tocEntity.page_name : '',
                } as HistoryItemMetadata),
                page_name: 'page_name' in tocEntity ? tocEntity.page_name : null,
              };
            }
            break;
          case HistoryItemType.DECKLINE:
            if ('__typename' in entity) {
              const { __typename: typeName } = entity;
              if (typeName === 'Deckline') {
                decklineEntity = entity as EnhancedDeckline;
              }
            } else {
              decklineEntity = entity as DeleteDecklineData;
            }

            if (decklineEntity) {
              newAuditHistoryId = `${currentGranularityPage?.page_name}_${selectedGranularity}-${decklineEntity?.deckline_id}-${internalId}`;
              return {
                ...coreAuditHistoryDetails,
                pk: 'pk' in decklineEntity ? decklineEntity.pk : `DELETED_DECKLINE#${decklineEntity.deckline_id}`, // Not sent to api. Purely to fulfill the requirement of AuditHistory entity
                sk: `AUDIT#${newAuditHistoryId}`, // Not sent to api.
                row: JSON.stringify({
                  itemId: decklineEntity.deckline_id,
                  itemType: historyItemType,
                  displayName: 'kpi_name' in decklineEntity ? decklineEntity.kpi_name : '',
                } as HistoryItemMetadata),
                page_name: currentGranularityPage?.page_name,
              };
            }
            break;
          default:
          // No Default
        }
      }

      return null;
    },
    [currentGranularityPage?.page_name, currentPageId, selectedGranularity, username],
  );

  /**
   * Formats a field value for output in Audit history if required. Such as to provide clearer output for Booleans
   * with Deckline Formatting options
   * @param value - The field value to format
   */
  const formatFieldValue = (value: unknown) => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value ?? '');
  };

  /**
   * Returns the display values for a field change
   * @param beforeEntity - The entity before the change
   * @param afterEntity - The entity after the change
   * @param historyItemType - The type of history item being tracked
   * @param field - The field that was changed
   * @returns The display values for the field change
   */
  const getChangeDisplayValues = useCallback(
    (
      beforeEntity: AuditableEntity | null,
      afterEntity: AuditableEntity | null,
      historyItemType: HistoryItemType,
      field?: string,
    ) => {
      switch (historyItemType) {
        case HistoryItemType.PAGE: {
          if (beforeEntity && afterEntity && field) {
            const beforePage = beforeEntity as EnhancedTOCEntry;
            const afterPage = afterEntity as EnhancedTOCEntry;

            if (!(field in TOC_FORMATTING_FIELD_AUDIT_LABELS)) {
              return {
                before: get(beforePage, field, '') as string,
                after: get(afterPage, field, '') as string,
              };
            }
            return {
              before: `${TOC_FORMATTING_FIELD_AUDIT_LABELS[field as TOCFormattingFields]}: ${formatFieldValue(get(beforePage, field, ''))}`,
              after: `${TOC_FORMATTING_FIELD_AUDIT_LABELS[field as TOCFormattingFields]}: ${formatFieldValue(get(afterPage, field, ''))}`,
            };
          }
          // Create event
          const page = afterEntity as EnhancedTOCEntry;
          return {
            before: null,
            after: `Page: ${page.page_name} with ID: ${page.page_id}`,
          };
        }
        case HistoryItemType.DECKLINE: {
          if (beforeEntity && afterEntity && field) {
            const beforeDeckline = beforeEntity as EnhancedDeckline;
            const afterDeckline = afterEntity as EnhancedDeckline;

            if (!(field in DECKLINE_FORMATTING_FIELD_AUDIT_LABELS)) {
              return {
                before: get(beforeDeckline, field, '') as string,
                after: get(afterDeckline, field, '') as string,
              };
            }
            return {
              before: `${DECKLINE_FORMATTING_FIELD_AUDIT_LABELS[field as DecklineFormattingFields]}: ${formatFieldValue(get(beforeDeckline, field, ''))}`,
              after: `${DECKLINE_FORMATTING_FIELD_AUDIT_LABELS[field as DecklineFormattingFields]}: ${formatFieldValue(get(afterDeckline, field, ''))}`,
            };
          }
          // A null beforeEntity and omission of field implies Create event
          const deckline = afterEntity as EnhancedDeckline;
          return {
            before: null,
            after: `Deckline: ${deckline.metadata.actual_kpi_name} with ID: ${deckline.deckline_id}`,
          };
        }
        default:
          return {
            before: 'No Change Value Description',
            after: 'No Change Value Description',
          };
      }
    },
    [],
  );

  /**
   * Reformats an entity into its specific type as per HistoryItemType to ensure type safety in downstream consumption
   * @param historyItemType - The history item type to reformat the entity to
   * @param entity - The entity to reformat
   * @returns The entity reformatted to its specific type
   */
  const getEntityAsType = useCallback((historyItemType: HistoryItemType, entity: AuditableEntity) => {
    const hasTypeNameField = '__typename' in entity;
    let entityTypeName: string | undefined;
    if (hasTypeNameField) {
      const { __typename: typeName } = entity;
      entityTypeName = typeName as string;
    }

    switch (historyItemType) {
      case HistoryItemType.PAGE:
        if (hasTypeNameField && !isNil(entityTypeName) && entityTypeName === 'TOCEntry') {
          return entity as unknown as EnhancedTOCEntry;
        }
        // Delete entity
        return entity as unknown as DeleteTOCEntryData;
      case HistoryItemType.DECKLINE:
        if (hasTypeNameField && !isNil(entityTypeName) && entityTypeName === 'Deckline') {
          return entity as unknown as EnhancedDeckline;
        }
        // Delete entity
        return entity as unknown as DeleteDecklineData;

      default:
        return entity;
    }
  }, []);

  /**
   * Tracks a field change and adds it to the audit trail
   * @param beforeEntity - The entity before the change
   * @param afterEntity - The entity after the change
   * @param field - The field that was changed
   * @param historyItemType - The type of history item being tracked
   * @param changeType - The type of change being tracked
   * @param currAuditTrail - The current audit trail to add the change to
   * @returns The updated audit trail
   */
  const trackFieldChange = useCallback(
    (
      beforeEntity: AuditableEntity | null,
      afterEntity: AuditableEntity | null,
      field: string | undefined,
      historyItemType: HistoryItemType,
      changeType: AuditHistoryChangeType,
      currAuditTrail: EnhancedAuditHistory[],
    ) => {
      if (isNil(beforeEntity) && isNil(afterEntity)) return currAuditTrail;

      let auditEntity: AuditableEntity | null = null;
      if (afterEntity) {
        auditEntity = getEntityAsType(historyItemType, afterEntity);
      } else if (beforeEntity && isNil(afterEntity)) {
        auditEntity = getEntityAsType(historyItemType, beforeEntity);
      }

      if (auditEntity) {
        const baseAuditHistoryItem = createBaseAuditHistoryItem(
          auditEntity, // Changed object entity state as will always exist
          historyItemType,
          changeType,
          new Date().toISOString(),
        );

        if (!isNil(baseAuditHistoryItem)) {
          const changeDisplayValues = getChangeDisplayValues(beforeEntity, afterEntity, historyItemType, field);
          currAuditTrail.push({
            ...baseAuditHistoryItem,
            before: changeDisplayValues.before,
            after: changeDisplayValues.after,
          });
        }
      }

      return currAuditTrail;
    },
    [createBaseAuditHistoryItem, getChangeDisplayValues, getEntityAsType],
  );

  /**
   * Generates an audit trail for a given set of TOCEntries as part of the Save Changes request
   * @param originalTocEntries - The original TOC entries (before changes)
   * @param createTocEntries - The TOC entries that were created
   * @param updateTocEntries - The TOC entries that were updated
   * @param deleteTocEntries - The TOC entries that were deleted
   * @returns The generated audit trail
   */
  const generatePageLibraryAuditTrail = useCallback(
    (
      originalTocEntries: EnhancedTOCEntry[],
      createTocEntries: EnhancedTOCEntry[],
      updateTocEntries: EnhancedTOCEntry[],
      deleteTocEntries: DeleteTOCEntryData[],
    ) => {
      const pageLibraryAuditTrail: EnhancedAuditHistory[] = [];
      const currentDateTime = new Date().toISOString(); // Matches expected AWSDateTime

      // Add create TOC entries to audit trail
      forEach(createTocEntries, (tocEntry) => {
        trackFieldChange(
          null,
          tocEntry,
          undefined,
          HistoryItemType.PAGE,
          AuditHistoryChangeType.PAGE_CREATED,
          pageLibraryAuditTrail,
        );
      });

      // Add update TOC entries to audit trail
      forEach(updateTocEntries, (tocEntry) => {
        const beforeTocEntry = find(
          originalTocEntries,
          (originalTocEntry) => originalTocEntry.toc_id === tocEntry.toc_id,
        );

        if (!beforeTocEntry) return;

        // Handle changes to TOC Page Name
        if (beforeTocEntry.toc_page_name !== tocEntry.toc_page_name) {
          trackFieldChange(
            beforeTocEntry,
            tocEntry,
            'toc_page_name',
            HistoryItemType.PAGE,
            AuditHistoryChangeType.PAGE_TOC_NAME_CHANGED,
            pageLibraryAuditTrail,
          );
        }

        // Handle Page Name change
        if (beforeTocEntry.page_name !== tocEntry.page_name) {
          trackFieldChange(
            beforeTocEntry,
            tocEntry,
            'page_name',
            HistoryItemType.PAGE,
            AuditHistoryChangeType.PAGE_NAME_CHANGED,
            pageLibraryAuditTrail,
          );
        }

        // Page Group changes
        if (beforeTocEntry.group_name !== tocEntry.group_name) {
          trackFieldChange(
            beforeTocEntry,
            tocEntry,
            'group_name',
            HistoryItemType.PAGE,
            AuditHistoryChangeType.PAGE_GROUP_CHANGED,
            pageLibraryAuditTrail,
          );
        }

        // Visibility Changes
        if (beforeTocEntry.is_visible !== tocEntry.is_visible) {
          trackFieldChange(
            beforeTocEntry,
            tocEntry,
            'is_visible',
            HistoryItemType.PAGE,
            AuditHistoryChangeType.PAGE_VISIBILITY_CHANGED,
            pageLibraryAuditTrail,
          );
        }

        // Active state Changes
        if (beforeTocEntry.is_active !== tocEntry.is_active) {
          trackFieldChange(
            beforeTocEntry,
            tocEntry,
            'is_active',
            HistoryItemType.PAGE,
            AuditHistoryChangeType.PAGE_ACTIVE_STATE_CHANGED,
            pageLibraryAuditTrail,
          );
        }
      });

      // Add Delete TOC Entries to audit trails
      forEach(deleteTocEntries, (tocEntry) => {
        const beforeTocEntry = find(
          originalTocEntries,
          (originalTocEntry) => originalTocEntry.toc_id === tocEntry.toc_id,
        );

        if (!beforeTocEntry) return;

        const baseAuditHistoryItem = createBaseAuditHistoryItem(
          { ...tocEntry, page_id: beforeTocEntry.page_id }, // Include known PageId as it is expected
          HistoryItemType.PAGE,
          AuditHistoryChangeType.PAGE_DELETED,
          currentDateTime,
        );

        if (!isNil(baseAuditHistoryItem)) {
          pageLibraryAuditTrail.push({
            ...baseAuditHistoryItem,
            before: `Page: ${beforeTocEntry?.page_name}`,
            after: 'Deleted',
          });
        }
      });

      // Persist the generated audit trail in context abd return it
      setAuditTrail(pageLibraryAuditTrail);

      return pageLibraryAuditTrail;
    },
    [createBaseAuditHistoryItem, trackFieldChange],
  );

  /**
   * Generates an audit trail for a given set of decklines as part of Save Changes request
   * @param originalDecklines - The original decklines (before changes)
   * @param createDecklines - The decklines that were created
   * @param updateDecklines - The decklines that were updated
   * @param deleteDecklines - The decklines that were deleted
   * @returns The generated audit trail
   */
  const generateDecklineAuditTrail = useCallback(
    (
      originalDecklines: EnhancedDeckline[],
      createDecklines: EnhancedDeckline[],
      updateDecklines: EnhancedDeckline[],
      deleteDecklines: DeleteDecklineData[],
    ) => {
      const decklineAuditTrail: EnhancedAuditHistory[] = [];
      const currentDateTime = new Date().toISOString(); // Matches expected AWSDateTime

      // Add create decklines to audit trail
      forEach(createDecklines, (deckline) => {
        trackFieldChange(
          null,
          deckline,
          undefined,
          HistoryItemType.DECKLINE,
          AuditHistoryChangeType.DECKLINE_CREATED,
          decklineAuditTrail,
        );
      });

      // Add update decklines to audit trail
      forEach(updateDecklines, (deckline) => {
        const beforeDeckline = find(
          originalDecklines,
          (originalDeckline) => originalDeckline.deckline_id === deckline.deckline_id,
        );

        if (!beforeDeckline) return;

        // Handle changes to Deckline Name
        if (beforeDeckline.metadata.actual_kpi_name !== deckline.metadata.actual_kpi_name) {
          trackFieldChange(
            beforeDeckline,
            deckline,
            'metadata.actual_kpi_name',
            HistoryItemType.DECKLINE,
            AuditHistoryChangeType.DECKLINE_NAME_CHANGED,
            decklineAuditTrail,
          );
        }

        // Handle changes to Deckline Type
        if (beforeDeckline.metadata.decklineType !== deckline.metadata.decklineType) {
          trackFieldChange(
            beforeDeckline,
            deckline,
            'metadata.decklineType',
            HistoryItemType.DECKLINE,
            AuditHistoryChangeType.DECKLINE_TYPE_CHANGED,
            decklineAuditTrail,
          );
        }

        // Handle changes to formatting
        forEach(Object.values(DecklineFormattingFields), (formattingField) => {
          if (beforeDeckline[formattingField] !== deckline[formattingField]) {
            trackFieldChange(
              beforeDeckline,
              deckline,
              formattingField,
              HistoryItemType.DECKLINE,
              AuditHistoryChangeType.DECKLINE_FORMATTING_CHANGED,
              decklineAuditTrail,
            );
          }
        });

        // Handle changes to KPI (KPI ID and KPI Formula are linked, so only track an entry here if KPI formula is unchanged)
        if (beforeDeckline.kpi_id !== deckline.kpi_id && beforeDeckline.kpi_formula === deckline.kpi_formula) {
          trackFieldChange(
            beforeDeckline,
            deckline,
            'kpi_id',
            HistoryItemType.DECKLINE,
            AuditHistoryChangeType.KPI_CHANGED,
            decklineAuditTrail,
          );
        }

        // Handle changes to KPI Formula
        if (beforeDeckline.kpi_formula !== deckline.kpi_formula) {
          trackFieldChange(
            beforeDeckline,
            deckline,
            'kpi_formula',
            HistoryItemType.DECKLINE,
            AuditHistoryChangeType.KPI_FORMULA_CHANGED,
            decklineAuditTrail,
          );
        }

        // Handle changes to Subtotal Formula
        if (beforeDeckline.subtotal_formula !== deckline.subtotal_formula) {
          trackFieldChange(
            beforeDeckline,
            deckline,
            'subtotal_formula',
            HistoryItemType.DECKLINE,
            AuditHistoryChangeType.SUBTOTAL_FORMULA_CHANGED,
            decklineAuditTrail,
          );
        }
      });

      // Add delete decklines to audit trail
      forEach(deleteDecklines, (deckline) => {
        const beforeDeckline = find(
          originalDecklines,
          (originalDeckline) => originalDeckline.deckline_id === deckline.deckline_id,
        );

        if (!beforeDeckline) return;

        const baseAuditHistoryItem = createBaseAuditHistoryItem(
          deckline,
          HistoryItemType.DECKLINE,
          AuditHistoryChangeType.DECKLINE_DELETED,
          currentDateTime,
        );

        if (!isNil(baseAuditHistoryItem)) {
          decklineAuditTrail.push({
            ...baseAuditHistoryItem,
            before: `Deckline: ${beforeDeckline?.metadata.actual_kpi_name}`,
            after: 'Deleted',
          });
        }
      });

      // Persist the generated audit trail in context, and return it
      setAuditTrail(decklineAuditTrail);

      return decklineAuditTrail;
    },
    [createBaseAuditHistoryItem, trackFieldChange],
  );

  /**
   * Formats the Audit History data to match the expected format for display
   * @param auditHistory - An array of Audit History data to format
   * @returns The formatted Audit History data
   */
  const formatAuditHistory = useCallback((auditHistory: AuditHistory[]) => {
    return auditHistory.map((auditHistoryItem: AuditHistory) => {
      let rowMetadata: HistoryItemMetadata | string = '';

      if (auditHistoryItem?.row) {
        try {
          rowMetadata = JSON.parse(auditHistoryItem.row) as HistoryItemMetadata;
        } catch {
          rowMetadata = auditHistoryItem.row;
        }
      }

      return {
        ...auditHistoryItem,
        row: rowMetadata,
      };
    });
  }, []);

  /**
   * Saves the Audit Trail for the current Page
   *
   * @param auditTrailData - The generated Audit Trail data to persist
   */
  const saveAuditTrail = useCallback(
    (auditTrailData: EnhancedAuditHistory[]) => {
      setIsSaving(true);
      if (!isEmpty(auditTrailData)) {
        // Format a change_set to create Audit History
        const auditHistoryChangeSet = {
          [ChangeSetTargets.audit]: {
            create: auditTrailData,
          },
        };

        try {
          const validateAuditHistoryChangeSet: SaveDeckChangeSetType =
            SaveDeckChangeSetSchema.parse(auditHistoryChangeSet);
          // Create payload to Save Audit History
          const saveAuditHistoryParams: UseSaveDeckParams = {
            deckId: currentDeckId || undefined,
            deckName: currentDeck?.deck_name || undefined,
            change_set: validateAuditHistoryChangeSet,
            currentUser: username,
            successCallback: async () => {
              toastNotification({
                message: 'Audit History Updated',
                visible: true,
                type: 'info',
              });
            },
          };
          // Save Audit History to backend
          saveDeckMutation.mutate(saveAuditHistoryParams, {
            onSuccess: () => {
              setIsSaving(false);
            },
            onError: (saveError) => {
              setIsSaving(false);
              console.error('Save operation failed:', saveError);
              toastNotification({
                message: 'Save Audit History Error Has Occurred',
                visible: true,
                type: 'error',
              });
            },
          });
        } catch (auditValidationError) {
          console.error('SaveDeck changeset validation for Audits failed', auditValidationError);
        }
      } else {
        // No Audit changes to save
        setIsSaving(false);
      }
    },
    [currentDeckId, currentDeck?.deck_name, username, toastNotification, saveDeckMutation],
  );

  /**
   * Effect to reset audit history when deck and page change
   */
  useEffect(() => {
    resetAuditHistory();
  }, [currentDeckId, currentPageId, resetAuditHistory]);

  /**
   * Effect to Set Deck Audit History data once available
   */
  useEffect(() => {
    if (deckAuditHistoryData) {
      // Format and set Deck Audit History
      const formattedDeckAuditHistoryData = formatAuditHistory(deckAuditHistoryData);
      setDeckAuditHistory(formattedDeckAuditHistoryData);
      // Clear any pending audit trail on new Deck Audit data
      setAuditTrail([]);
    }
  }, [deckAuditHistoryData, formatAuditHistory]);

  /**
   * Effect to Set Page Audit History data once available
   */
  useEffect(() => {
    if (pageAuditHistoryData) {
      // Flatten paginated results and format
      const allItems = pageAuditHistoryData.pages.flatMap((page) => page.items);
      const formattedPageAuditHistoryData = formatAuditHistory(allItems);
      setPageAuditHistory(formattedPageAuditHistoryData);
      // Only clear audit trail on initial page load, not on subsequent page fetches
      if (pageAuditHistoryData.pages.length === 1) {
        setAuditTrail([]);
      }
    }
  }, [formatAuditHistory, pageAuditHistoryData]);

  // Combine loading states
  const isLoading = isDeckAuditHistortLoading || isPageAuditHistortLoading;

  // Combine error states
  const isError = isDeckAuditHistoryError || isPageAuditHistoryError;
  const error = deckAuditHistoryError || pageAuditHistoryError;

  /**
   * Creates the Audit History context value object
   *
   * Combines all stage variables and methods into a single object that will be
   * provided to consumers of the context.
   */
  const contextValue = useMemo<AuditHistoryContextType>(
    () => ({
      deckAuditHistory,
      pageAuditHistory,
      auditTrail,
      setDeckAuditHistory,
      setPageAuditHistory,
      setAuditTrail,
      generatePageLibraryAuditTrail,
      generateDecklineAuditTrail,
      saveAuditTrail,
      isSavingAuditHistory: isSaving,
      isLoading,
      isError,
      error: error as Error | null,
      fetchNextPageAuditHistory: fetchNextPage,
      hasNextPageAuditHistory: hasNextPage ?? false,
      isFetchingNextPageAuditHistory: isFetchingNextPage,
    }),
    [
      deckAuditHistory,
      pageAuditHistory,
      auditTrail,
      generatePageLibraryAuditTrail,
      generateDecklineAuditTrail,
      saveAuditTrail,
      isSaving,
      isLoading,
      isError,
      error,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    ],
  );

  return <AuditHistoryContext value={contextValue}>{children}</AuditHistoryContext>;
};

/**
 * Custom hook to access the Audit History context
 *
 * @returns {AuditHistoryContextType} The Audit History context value
 */
export const useAuditHistoryContext = (): AuditHistoryContextType => {
  return use(AuditHistoryContext);
};

export default AuditHistoryContext;
